import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload, ImageIcon } from "lucide-react";

const empty = { name: "", description: "", price: 0, weight: "", category: "fresh", images: [], in_stock: true };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/products").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => { setEditing(p); setForm({ ...p, images: p.images || [], variants: p.variants || [] }); };
  const startNew = () => { setEditing("new"); setForm(empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, price: parseFloat(form.price) };
      if (editing === "new") await api.post("/products", body);
      else await api.put(`/products/${editing.id}`, body);
      toast.success("Saved");
      close(); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const toggleStock = async (p) => {
    await api.put(`/products/${p.id}`, { ...p, in_stock: !p.in_stock });
    load();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.images.length + files.length > 5) {
      toast.error("Maximum 5 images per product");
      return;
    }
    setUploading(true);
    try {
      const newUrls = [];
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} is over 5MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...newUrls] }));
      if (newUrls.length) toast.success(`Uploaded ${newUrls.length} image${newUrls.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const addImageUrl = () => {
    const url = window.prompt("Paste image URL");
    if (url && url.trim()) {
      setForm((prev) => ({ ...prev, images: [...prev.images, url.trim()] }));
    }
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [...(prev.variants || []), { name: "", price: prev.price || 0, weight: prev.weight || "" }],
    }));
  };
  const updateVariant = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === idx ? { ...v, [field]: value } : v),
    }));
  };
  const removeVariant = (idx) => {
    setForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl text-[color:var(--color-primary)]">Products</h1>
        <button onClick={startNew} className="btn-accent" data-testid="add-product-btn"><Plus className="w-4 h-4"/> New Product</button>
      </div>

      <div className="bg-white border border-[color:var(--color-border)] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Price</th>
              <th className="text-left p-3">Stock</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[color:var(--color-border)]">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-10 h-10 object-cover rounded"/>}
                    <span className="font-medium">{p.name}</span>
                  </div>
                </td>
                <td className="p-3 capitalize">{p.category}</td>
                <td className="p-3">£{p.price.toFixed(2)}</td>
                <td className="p-3">
                  <button onClick={() => toggleStock(p)} className={`text-xs uppercase tracking-widest px-2 py-1 rounded ${p.in_stock ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`} data-testid={`toggle-stock-${p.id}`}>
                    {p.in_stock ? "In Stock" : "Out"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(p)} className="p-1.5 text-[color:var(--color-primary)] hover:bg-gray-100 rounded" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4"/></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded ml-2" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-500">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="product-form-modal">
          <div className="bg-white rounded-lg p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">{editing === "new" ? "New Product" : "Edit Product"}</h2>
              <button onClick={close}><X className="w-5 h-5"/></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="prod-name"/>
              <textarea required placeholder="Description" rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="prod-desc"/>
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" step="0.01" placeholder="Price (£)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="prod-price"/>
                <input placeholder="Weight (e.g. 1 lb)" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="prod-weight"/>
              </div>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="prod-category">
                <option value="fresh">Fresh</option>
                <option value="smoked">Smoked</option>
                <option value="dried">Dried</option>
                <option value="frozen">Frozen</option>
              </select>

              {/* Image management */}
              <div className="border border-[color:var(--color-border)] rounded p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-gray-600 flex items-center gap-1"><ImageIcon className="w-3 h-3"/> Product Images ({form.images.length}/5)</div>
                  <div className="flex gap-2">
                    <label className="text-xs px-3 py-1.5 bg-[color:var(--color-primary)] text-white rounded cursor-pointer hover:bg-[color:var(--color-primary-hover)] inline-flex items-center gap-1" data-testid="upload-image-btn">
                      <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        onChange={handleFiles}
                        disabled={uploading || form.images.length >= 5}
                        className="hidden"
                        data-testid="prod-file-input"
                      />
                    </label>
                    <button type="button" onClick={addImageUrl} className="text-xs px-3 py-1.5 border border-[color:var(--color-border)] rounded hover:bg-gray-50" data-testid="add-image-url-btn">Or paste URL</button>
                  </div>
                </div>
                {form.images.length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-6 border-2 border-dashed border-[color:var(--color-border)] rounded">No images yet. Upload from your device or paste a URL.</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative group aspect-square rounded overflow-hidden border border-[color:var(--color-border)]" data-testid={`prod-image-${i}`}>
                        <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                          data-testid={`remove-image-${i}`}
                          aria-label="Remove image"
                        >
                          <X className="w-3 h-3"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} data-testid="prod-instock"/>
                In stock
              </label>

              {/* Size variants */}
              <div className="border border-[color:var(--color-border)] rounded p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-gray-600">Size Variants ({(form.variants || []).length})</div>
                  <button type="button" onClick={addVariant} className="text-xs px-3 py-1.5 bg-[color:var(--color-primary)] text-white rounded hover:bg-[color:var(--color-primary-hover)]" data-testid="add-variant-btn">+ Add size</button>
                </div>
                {(form.variants || []).length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-3">
                    No size options. Customers will see the price above. Add sizes (e.g., Small/Large) to offer multiple variants.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {form.variants.map((v, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-row-${i}`}>
                        <input
                          required
                          placeholder="Size (e.g. Small)"
                          value={v.name || ""}
                          onChange={(e) => updateVariant(i, "name", e.target.value)}
                          className="col-span-5 border border-[color:var(--color-border)] rounded px-2 py-1.5 text-sm"
                          data-testid={`variant-name-${i}`}
                        />
                        <input
                          required type="number" step="0.01" min="0"
                          placeholder="Price (£)"
                          value={v.price}
                          onChange={(e) => updateVariant(i, "price", parseFloat(e.target.value || "0"))}
                          className="col-span-3 border border-[color:var(--color-border)] rounded px-2 py-1.5 text-sm"
                          data-testid={`variant-price-${i}`}
                        />
                        <input
                          placeholder="Weight"
                          value={v.weight || ""}
                          onChange={(e) => updateVariant(i, "weight", e.target.value)}
                          className="col-span-3 border border-[color:var(--color-border)] rounded px-2 py-1.5 text-sm"
                          data-testid={`variant-weight-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeVariant(i)}
                          className="col-span-1 text-red-500 hover:bg-red-50 rounded p-1.5"
                          data-testid={`remove-variant-${i}`}
                          aria-label="Remove variant"
                        >
                          <X className="w-4 h-4 mx-auto"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={close} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-primary flex-1 justify-center" data-testid="prod-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
